import React from "react";
import { Link } from "react-router-dom";
import { Snowflake, Star, GitCompareArrows } from "lucide-react";
import { isFavorite, toggleFavorite, getCompareIds, toggleCompare } from "@/lib/storage";
import { StationLogo } from "@/components/StationLogo";

/**
 * StationCard — versión minimalista.
 *
 * Sólo lo esencial: nombre, temperatura y una línea de estado.
 * Los detalles (viento, humedad, cota, sky-score…) están en el detalle.
 */
export const StationCard = ({ data, onChange }) => {
  const {
    station,
    weather,
    ski,
    available = true,
    _stale = false,
  } = data;

  const [fav, setFav] = React.useState(isFavorite(station.id));
  const [inCompare, setInCompare] = React.useState(getCompareIds().includes(station.id));

  React.useEffect(() => {
    const onF = () => setFav(isFavorite(station.id));
    const onC = () => setInCompare(getCompareIds().includes(station.id));
    window.addEventListener("creep:favorites", onF);
    window.addEventListener("creep:compare", onC);
    return () => {
      window.removeEventListener("creep:favorites", onF);
      window.removeEventListener("creep:compare", onC);
    };
  }, [station.id]);

  const temp = weather?.temperature != null ? Math.round(weather.temperature) : null;
  const status = !available
    ? "No disponible"
    : ski?.skiable
    ? "Se puede esquiar"
    : weather?.sky || "—";

  return (
    <Link
      to={`/station/${station.id}`}
      data-testid={`station-card-${station.id}`}
      className="group relative block rounded-2xl px-5 py-5 transition-colors"
      style={{
        background: "rgba(11,11,18,0.55)",
        border: "1px solid rgba(255,255,255,0.06)",
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Acciones (favorito / comparador) — solo visibles en hover para mantener limpieza */}
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          aria-label="Favorito"
          data-testid={`fav-btn-${station.id}`}
          onClick={(e) => { e.preventDefault(); toggleFavorite(station.id); onChange?.(); }}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
            fav
              ? "bg-violet-500/25 text-violet-100"
              : "bg-white/5 text-white/60 hover:text-white"
          }`}
        >
          <Star size={12} fill={fav ? "currentColor" : "none"} />
        </button>
        <button
          aria-label="Comparar"
          data-testid={`compare-btn-${station.id}`}
          onClick={(e) => { e.preventDefault(); toggleCompare(station.id); onChange?.(); }}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
            inCompare
              ? "bg-violet-500/25 text-violet-100"
              : "bg-white/5 text-white/60 hover:text-white"
          }`}
        >
          <GitCompareArrows size={12} />
        </button>
      </div>

      {/* Región + logo (marca discreta) */}
      <div className="flex items-center gap-3">
        <StationLogo id={station.id} size={44} />
        <div className="min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.28em] text-white/40"
            data-testid={`card-region-${station.id}`}
          >
            {station.region}
          </div>
          <div className="font-extrabold text-xl tracking-tight text-white leading-tight truncate">
            {station.name}
          </div>
        </div>
      </div>

      {/* Temperatura o "—" */}
      <div className="mt-5 flex items-baseline gap-1.5">
        <span
          className="text-5xl font-extrabold text-white leading-none tracking-tight"
          data-testid={`card-temp-${station.id}`}
        >
          {temp != null ? temp : "—"}
        </span>
        <span className="text-lg text-white/40 leading-none">°</span>
        {_stale && (
          <span
            data-testid={`stale-badge-${station.id}`}
            title="Última lectura válida mientras la API se restablece"
            className="ml-auto text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-200"
          >
            caché
          </span>
        )}
      </div>

      {/* Estado (una línea) */}
      <div
        className={`mt-3 text-xs ${
          !available
            ? "text-amber-200/80"
            : ski?.skiable
            ? "text-[#8A6BFF]"
            : "text-white/50"
        } flex items-center gap-1.5`}
        data-testid={`card-status-${station.id}`}
      >
        {available && ski?.skiable && <Snowflake size={11} />}
        <span>{status}</span>
      </div>
    </Link>
  );
};

export default StationCard;
